import type {
  LegacyChoiceResult,
  MultipleChoiceStaticContent,
} from '@core-api/presentation/types/response';
import type { ChoicesT, EmptyT } from '@mentimeter/question-modules-types';
import { useMemo } from 'react';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../../legacy-context/presentationContext';
import { getVisualizationState } from '../../../utils/getVisualizationState';
import type { Choice, Segmentation } from '../types';
import { useChoicePreviewResultsLegacy } from './use-choice-preview-results-legacy';
import { useChoiceResults } from './use-choice-results';

/**
 * `SlideStateChoices` is a `type` because an `interface`
 * can't satisfy the generic used within `useSlideState`.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type SlideStateChoices = { reveal?: boolean };

interface UseChoicesPresentationReturn {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: MultipleChoiceStaticContent['textSizeOffset'];
  identifiedResults: LegacyChoiceResult[] | null;
  segmentation: Segmentation;
  shouldShowCorrect: boolean;
  results: ChoicesT | EmptyT;
  choices: Choice[];
  visualizationType: MultipleChoiceStaticContent['interactiveContentStyling']['visualization'];
  shouldHideResults: boolean;
  shouldShowPercent: boolean;
  shouldAnimate: boolean;
  CanvasVisualizationArea: NonNullable<
    ReturnType<typeof usePresentationContext>['InteractiveCanvasComponents']
  >['CanvasVisualizationArea'];
}

export function useChoicesPresentation(): UseChoicesPresentationReturn {
  const {
    useSeries,
    useSegmentation,
    useConfig,
    useSlideState,
    useResults,
    isPreviewResultsEnabled,
    useCurrentSession,
    usePace,
    InteractiveCanvasComponents,
  } = usePresentationContext();
  const slide = usePresentationSlideContext();
  const { participation_identity_mode, participation_identity_visibility } =
    useSeries();
  const {
    isSegmented,
    segmentedQuestionTitle = '',
    segments = [],
  } = useSegmentation();
  const { currentStep } = usePace();

  const { mode, enableAnimations } = useConfig();
  const [{ reveal }] = useSlideState<SlideStateChoices>();

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const staticContent = slide.staticContent as MultipleChoiceStaticContent;
  const interactiveContent = slide.interactiveContents[0];

  const shouldShowCorrect =
    interactiveContent?.correctAnswerMode === 'enabled' &&
    (reveal || ['screenshot', 'public', 'idle'].includes(mode));

  const results = useResults<ChoicesT>();
  const { isCurrentSessionActive, currentSessionId } = useCurrentSession();

  const isSubTypeWithIdentifiedResponses =
    staticContent.interactiveContentStyling.visualization !== 'dots';

  const hasIdentifiedResultsSettingOnAndVisible =
    participation_identity_mode === 'identified' &&
    participation_identity_visibility === 'everyone';

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    visualizationState === 'hide-answers';

  const shouldFetchIdentifiedResults =
    hasIdentifiedResultsSettingOnAndVisible &&
    isSubTypeWithIdentifiedResponses &&
    isCurrentSessionActive &&
    !shouldHideResults &&
    !isPreviewResultsEnabled;

  const choiceOptions: Choice[] = useMemo(
    () =>
      slide.interactiveContents[0]!.choices.map((choice, index) => ({
        id: choice.legacyChoiceId ?? index + 1,
        label: choice.title,
        imageUrl: choice.image?.presets.original.url,
        isMarkedCorrect: choice.markedCorrect,
      })),
    [slide.interactiveContents],
  );

  const { identifiedResults } = useChoiceResults({
    adminKey: slide.slideAdminKey,
    shouldFetchResults: shouldFetchIdentifiedResults,
    sessionId: isCurrentSessionActive ? undefined : currentSessionId,
  });

  const legacyPreviewResults = useChoicePreviewResultsLegacy({
    isPreviewResultsEnabled,
    choices: choiceOptions,
    segments: segments.length ? segments : undefined,
  });

  let resultsToShow;

  if (isPreviewResultsEnabled) {
    resultsToShow = legacyPreviewResults;
  } else {
    resultsToShow = results;
  }

  return {
    title: slide.title,
    styledTitle: staticContent.styledTitle as Record<string, unknown>,
    label: staticContent.label,
    longerDescription: slide.interactiveContents[0]!.description,
    fontSizeOffset: staticContent.textSizeOffset,
    identifiedResults,
    segmentation: {
      isSegmented,
      segmentedQuestionTitle,
      segments,
    },
    shouldShowCorrect:
      shouldShowCorrect || visualizationState === 'view-correct-answer',
    results: resultsToShow,
    choices: choiceOptions,
    visualizationType: staticContent.interactiveContentStyling.visualization,
    shouldHideResults,
    shouldShowPercent:
      staticContent.interactiveContentStyling.responseCountDisplayMode ===
      'percentage',
    shouldAnimate: enableAnimations,
    CanvasVisualizationArea:
      InteractiveCanvasComponents?.CanvasVisualizationArea,
  };
}
